import { InputProps } from 'common/designSystem/input/types';
import React, { useState, useEffect } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import PasswordChecklist from 'react-password-checklist';
import { useAppDispatch, useAppSelector } from 'common/hooks/state';
import { setUserData } from 'common/store/appSlice';
import ValidationIndicator from 'common/designSystem/tdinput/ValidationIndicator';
import { RootState } from '../../store/store';

export const Password = ({
  labelText, id, register, error, placeholder, errorMessage, value = '', isTick = false,
  showPasswordRule = true, name = 'password',
}: InputProps) => {
  const [passwordType, setPasswordType] = useState('password');
  const [showTick, setShowTick] = useState(isTick);
  const user = useAppSelector((state: RootState) => state.app.user);
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (value) {
      dispatch(setUserData({
        user: {
          ...user,
          password: value,
        },
      }));
    }
  }, [value]);

  const togglePassword = () => {
    setPasswordType((prev) => (prev === 'password' ? 'text' : 'password'));
  };

  const handleTick = (isMatchedCriteria: boolean) => {
    setShowTick(isMatchedCriteria);
  };

  const showInvalid = !!error;
  const showValid = !showInvalid && (showTick || isTick);
  const stateClass = showInvalid
    ? 'border-2 border-doqit-highlight-pink text-doqit-highlight-pink placeholder:text-doqit-highlight-pink'
    : 'border border-doqit-purple-150 text-doqit-deep-purple placeholder:text-doqit-mid-purple';
  const baseInputClass = 'h-[71px] rounded-[12px] font-body font-medium text-[19px] leading-[19px] tracking-[0.5px] bg-doqit-purple-25 outline-none placeholder:opacity-70';
  const insetShadow = 'shadow-[inset_2px_2px_4px_rgba(57,38,79,0.2)]';
  const inputClass = `block w-full pl-[21px] pr-[55px] ${baseInputClass} ${insetShadow} ${stateClass}`;

  return (
    <label htmlFor={id} className="block">
      <span className="block text-doqit-mid-purple text-[15px] leading-[19px] tracking-[0.5px] font-medium font-body mb-1.5">
        {labelText}
      </span>
      <div className="relative">
        <input
          id={id}
          name={name}
          type={passwordType}
          {...register(name)}
          placeholder={placeholder}
          className={inputClass}
        />
        <button
          type="button"
          onClick={togglePassword}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-doqit-mid-purple/70 hover:text-doqit-mid-purple focus:outline-none"
          aria-label={passwordType === 'password' ? 'Show password' : 'Hide password'}
        >
          {passwordType === 'password'
            ? <EyeSlashIcon className="h-6 w-6" />
            : <EyeIcon className="h-6 w-6" />}
        </button>
        {showValid && (
          <ValidationIndicator state="valid" className="absolute right-3 -top-[10px]" />
        )}
        {showInvalid && (
          <ValidationIndicator state="invalid" className="absolute right-3 -top-[10px]" />
        )}
      </div>
      {showInvalid && errorMessage && (
        <p className={`${name}-error mt-1.5 text-doqit-highlight-pink text-[15px] leading-[19px] tracking-[0.5px] font-bold font-body`}>
          {errorMessage}
        </p>
      )}
      {showPasswordRule && value && (
        <div className="mt-2">
          <span className="font-semibold text-doqit-mid-purple text-[15px]">Password must meet following requirements</span>
          <PasswordChecklist
            rules={['minLength', 'number', 'capital', 'specialChar']}
            minLength={12}
            value={value}
            messages={{
              minLength: 'Minimum 12 characters required',
              number: 'At least 1 number required',
              capital: 'At least 1 capital letter',
              specialChar: 'At least 1 symbol, for example ! ? £ . @ #',
            }}
            onChange={(isValid) => handleTick(isValid)}
          />
        </div>
      )}
    </label>
  );
};

export default Password;
